import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ShoppingBag, MapPin, PlusCircle, Search as SearchIcon, Store, User } from "lucide-react";
import { toast } from "sonner";

export const CATEGORY_META = {
  furniture:       "Furniture",
  electronics:     "Electronics",
  clothing:        "Clothing",
  baby_kids:       "Baby & Kids",
  home_garden:     "Home & Garden",
  sports_outdoors: "Sports & Outdoors",
  vehicles:        "Vehicles",
  tools:           "Tools",
  collectibles:    "Collectibles",
  free_stuff:      "Free stuff",
  other:           "Other",
};

const CONDITION_META = {
  new: "New — sealed / tags on",
  like_new: "Used — like new",
  good: "Used — good",
  fair: "Used — fair",
  for_parts: "For parts",
};

export function formatPrice(cents, currency = "USD") {
  if (cents === 0) return "Free";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(cents / 100);
}

export default function Marketplace() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("scope") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [sellerType, setSellerType] = useState(params.get("seller_type") || "");
  const [sort, setSort] = useState(params.get("sort") || "recent");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [activeNb, setActiveNb] = useState(params.get("gid") || "");

  useEffect(() => {
    if (user?.role !== "customer") return;
    (async () => {
      try {
        const r = await api.get("/neighborhoods/mine");
        const nb = r.data?.items || [];
        setNeighborhoods(nb);
        if (!activeNb && nb.length) setActiveNb(nb[0].id);
      } catch (e) {
        // Not a customer or no groups yet — leave empty; the tab hides itself.
      }
    })();
  }, [user?.id]);

  // Push filter state to the URL so the feed is shareable.
  useEffect(() => {
    const p = new URLSearchParams();
    if (tab !== "all") p.set("scope", tab);
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (sellerType) p.set("seller_type", sellerType);
    if (sort !== "recent") p.set("sort", sort);
    if (tab === "neighborhood" && activeNb) p.set("gid", activeNb);
    setParams(p, { replace: true });
  }, [tab, q, category, sellerType, sort, activeNb]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        let r;
        if (tab === "neighborhood") {
          if (!activeNb) { setItems([]); setTotal(0); return; }
          r = await api.get(`/marketplace/listings/neighborhood/${activeNb}`);
        } else {
          const qs = new URLSearchParams();
          if (q) qs.set("q", q);
          if (category) qs.set("category", category);
          if (sellerType) qs.set("seller_type", sellerType);
          if (sort) qs.set("sort", sort);
          // Marketplace shows only fixed-price + offer listings. Auctions
          // live on their own dedicated /auctions surface.
          qs.set("listing_type", "fixed");
          r = await api.get(`/marketplace/listings?${qs.toString()}`);
        }
        if (!alive) return;
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [tab, q, category, sellerType, sort, activeNb]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="marketplace-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#B31E1E]">Marketplace</div>
          <h1 className="font-display text-4xl tracking-tighter mt-1">Buy &amp; sell locally</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            List anything from furniture to electronics. Message sellers directly — arrange pickup or shipping off-platform.
          </p>
        </div>
        {user ? (
          <div className="flex gap-2 flex-wrap">
            <Link to="/marketplace/my"><Button variant="outline" className="rounded-[35px]" data-testid="my-listings-button">My listings</Button></Link>
            <Link to="/marketplace/sell"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="sell-button"><PlusCircle size={14} className="mr-2" /> Sell an item</Button></Link>
          </div>
        ) : (
          <Link to="/login"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Sign in to sell</Button></Link>
        )}
      </header>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="all" data-testid="tab-all">Everything</TabsTrigger>
            {user?.role === "customer" && (
              <TabsTrigger value="neighborhood" data-testid="tab-neighborhood">In my neighborhood</TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        {tab === "neighborhood" && (
          <Select value={activeNb} onValueChange={setActiveNb}>
            <SelectTrigger className="rounded-sm h-9 w-56" data-testid="nb-select"><SelectValue placeholder="Choose neighborhood" /></SelectTrigger>
            <SelectContent>
              {neighborhoods.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">Join a neighborhood first.</div>}
              {neighborhoods.map(n => <SelectItem key={n.id} value={n.id}>{n.name} — {n.city}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_180px_180px_140px] gap-3">
        <div className="relative">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search items…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="mp-search-input"
            className="rounded-sm pl-9"
          />
        </div>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm" data-testid="mp-category-select"><SelectValue placeholder="Any category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any category</SelectItem>
            {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sellerType || "all"} onValueChange={(v) => setSellerType(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm" data-testid="mp-seller-select"><SelectValue placeholder="Any seller" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any seller</SelectItem>
            <SelectItem value="customer">Individuals</SelectItem>
            <SelectItem value="business">Businesses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="rounded-sm" data-testid="mp-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Newest</SelectItem>
            <SelectItem value="price_asc">Price ↑</SelectItem>
            <SelectItem value="price_desc">Price ↓</SelectItem>
            <SelectItem value="popular">Most viewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="rounded-md p-10 text-center text-gray-600" data-testid="mp-empty">
            <ShoppingBag className="mx-auto mb-2 text-gray-400" size={28} />
            No listings yet — {user ? <Link to="/marketplace/sell" className="text-[#B31E1E] hover:underline">be the first to sell</Link> : "sign in to list an item"}.
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="mp-grid">
              {items.map(l => <ListingCard key={l.id} l={l} />)}
            </div>
            <div className="mt-4 text-xs font-mono text-gray-400">Showing {items.length} of {total}</div>
          </>
        )}
      </div>
    </div>
  );
}

export function ListingCard({ l }) {
  const cover = l.photos?.[0] || "";
  const isAuction = l.listing_type === "auction";
  // Compact countdown label for the card (no per-second updates — refresh on nav)
  const cdLabel = (() => {
    if (!isAuction || !l.ends_at) return null;
    const ms = new Date(l.ends_at).getTime() - Date.now();
    if (ms <= 0) return "Ended";
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h left`;
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
  })();
  const displayCents = isAuction ? (l.current_bid_cents || l.starting_bid_cents || 0) : l.price_cents;
  return (
    <Link to={`/marketplace/listings/${l.id}`} data-testid={`listing-card-${l.id}`} className="group">
      <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors">
        <div className="aspect-square bg-gray-100 relative">
          {cover ? (
            <img src={cover} alt={l.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <ShoppingBag size={32} />
            </div>
          )}
          {isAuction && (
            <Badge className="absolute top-2 right-2 rounded-sm bg-[#B31E1E] text-white text-[10px]" data-testid={`auction-badge-${l.id}`}>
              Auction · {cdLabel}
            </Badge>
          )}
          {l.videos?.length > 0 && (
            <span
              className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-sm bg-black/60 backdrop-blur text-white text-[10px] font-mono px-1.5 py-0.5"
              data-testid={`video-badge-${l.id}`}
              title={`${l.videos.length} video${l.videos.length !== 1 ? "s" : ""}`}
            >
              ▶ {l.videos.length}
            </span>
          )}
          {!isAuction && l.negotiable && (
            <Badge className="absolute top-2 right-2 rounded-sm bg-white/90 text-gray-800 border border-gray-200 hover:bg-white text-[10px]">OBO</Badge>
          )}
          {!isAuction && l.accepts_offers && !l.negotiable && (
            <Badge className="absolute top-2 right-2 rounded-sm bg-white/90 text-[#B31E1E] border border-[#B31E1E]/30 hover:bg-white text-[10px]">Offers</Badge>
          )}
          {l.seller_type === "business" && (
            <Badge className="absolute top-2 left-2 rounded-sm bg-[#B31E1E] text-white text-[10px]">
              <Store size={9} className="mr-1" /> Business
            </Badge>
          )}
        </div>
        <div className="p-3">
          <div className="font-medium text-gray-900 truncate">
            {formatPrice(displayCents, l.currency)}
            {isAuction && <span className="text-[10px] text-gray-500 ml-1 font-normal">· {l.bid_count || 0} bids</span>}
          </div>
          <div className="text-sm text-gray-700 truncate">{l.title}</div>
          <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1 truncate">
            <MapPin size={10} /> {l.city}{l.state ? `, ${l.state}` : ""}
          </div>
        </div>
      </Card>
    </Link>
  );
}
